import { FC } from 'react';
import { useAppDispatch } from 'src/hooks/redux-custom';
import { setSelectedCustomer } from 'src/store/slices/customer';
import { Customer, CustomerAttributes } from '@models';
import { useHistory } from 'react-router-dom';
import { DataGrid, GridCellParams, GridColDef, GridColumnHeaderParams } from '@mui/x-data-grid';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import { Typography } from '@mui/material';
import { useCustomer } from '@hooks/useCustomer';
import { showToast } from '@store/slices/notification';

type Props = {
  customers: Customer[];
};

export const SearchResults: FC<Props> = ({ customers }) => {
  const cust = useCustomer();
  const dispatch = useAppDispatch();
  const history = useHistory();

  // get customer termination date
  const getTerminatedDate = (customer: Customer) => {
    try {
      const terminationDateAttributeID = cust.customerAttributes.find(
        (attr: CustomerAttributes) => attr.name === 'accountterminateddate',
      )?.id;

      const terminatedDate = customer?.attributes.find(
        (attribute: { attribute_id: number | undefined }) => attribute.attribute_id === terminationDateAttributeID,
      )?.attribute_value as string;

      return terminatedDate;
    } catch (err) {
      console.error(err);
    }
  };

  // get customer terminated_by email
  const getTerminatedBy = (customer: Customer) => {
    try {
      const terminatedByAttributeID = cust.customerAttributes.find((attr: CustomerAttributes) => attr.name === 'accountterminatedby')?.id;

      const terminatedBy = customer?.attributes.find(
        (attribute: { attribute_id: number | undefined }) => attribute.attribute_id === terminatedByAttributeID,
      )?.attribute_value as string;

      return terminatedBy;
    } catch (err) {
      console.error(err);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      renderHeader: (params: GridColumnHeaderParams) => (
        <>
          <PersonIcon />
          <Typography variant="body1" sx={{ fontSize: 18, fontWeight: 600 }}>
            {'Name'}
          </Typography>
        </>
      ),
      flex: 1,
      renderCell: (params) => <Typography style={{ textDecoration: 'none', cursor: 'pointer' }}>{params.value}</Typography>,
      cellClassName: (params) => params.row.account_status,
    },
    {
      field: 'phone',
      renderHeader: (params: GridColumnHeaderParams) => (
        <>
          <PhoneIcon />
          <Typography variant="body1" sx={{ fontSize: 18, fontWeight: 600 }}>
            {'Phone Number'}
          </Typography>
        </>
      ),
      flex: 1,
      renderCell: (params) => <Typography style={{ textDecoration: 'none', cursor: 'pointer' }}>{params.value}</Typography>,
    },
    {
      field: 'email',
      renderHeader: (params: GridColumnHeaderParams) => (
        <>
          <EmailIcon />
          <Typography variant="body1" sx={{ fontSize: 18, fontWeight: 600 }}>
            {'Email'}
          </Typography>
        </>
      ),
      flex: 1.5,
      renderCell: (params) => <Typography style={{ textDecoration: 'none', cursor: 'pointer' }}>{params.value}</Typography>,
    },
    {
      field: 'company',
      renderHeader: (params: GridColumnHeaderParams) => (
        <>
          <BusinessIcon />
          <Typography variant="body1" sx={{ fontSize: 18, fontWeight: 600 }}>
            {'Company'}
          </Typography>
        </>
      ),
      flex: 1,
      renderCell: (params) => <Typography style={{ textDecoration: 'none', cursor: 'pointer' }}>{params.value}</Typography>,
    },
  ];

  const rows = customers?.map((customer) => {
    const terminatedDate = getTerminatedDate(customer);
    const terminatedBy = getTerminatedBy(customer);

    return {
      id: customer.id,
      name: `${customer.first_name} ${customer.last_name}`,
      phone: customer.phone,
      email: terminatedDate ? `Deleted By ${terminatedBy}` : customer.email,
      company: customer.company,
      account_status: terminatedDate ? 'terminated' : '',
    };
  });

  const showCustomerDetails = (cell: GridCellParams) => {
    /**
     * set the selected customer using dispatch redux
     */
    const selectedCustomer = customers.find((cust) => cust.id === cell.row.id);

    if (selectedCustomer) {
      const terminatedDate = getTerminatedDate(selectedCustomer);
      const terminatedBy = getTerminatedBy(selectedCustomer);

      // check if the customer profile terminated or not
      if (!terminatedDate) {
        dispatch(setSelectedCustomer(selectedCustomer));
        history.push(`/customer-detail/${cell.row.id}`);
      } else {
        dispatch(
          showToast({
            message: `Account Terminated on ` + new Date(terminatedDate).toDateString() + ` by ` + terminatedBy,
            type: 'error',
          }),
        );
      }
    }
  };

  return (
    <div style={{ marginTop: 10, marginBottom: 5 }} data-testid="customer-list-div">
      <DataGrid
        columns={columns}
        rows={rows}
        disableColumnFilter
        disableColumnSelector
        disableColumnMenu
        hideFooterPagination
        hideFooter
        onCellClick={showCustomerDetails}
        style={{ height: '100%', width: '100%' }}
        autoHeight
        data-testid="customer-list"
        sx={{
          '.MuiDataGrid-columnSeparator': {
            display: 'none',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#CD9BF0',
            color: '#161616',
          },
          '& .MuiDataGrid-row': {
            backgroundColor: '#FFFFFF',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: '#F4E4FF',
          },
          '& .terminated': {
            textDecoration: 'line-through',
          },
        }}
      />
    </div>
  );
};
